import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { SdProductComparatorStickyComponent } from '@seb/web-design-system/product-comparator';
import {
  IProductCard,
  ScrollSpyService,
  SdButtonComponent,
  SdModalDefaultComponent,
  StatusEnum
} from '@seb/web-design-system/core';
import { ComparedProductCard } from '@seb/web-common/core/models';
import { ProductComparatorService, ToasterService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-sticky-product-comparator',
  templateUrl: './sticky-product-comparator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./sticky-product-comparator.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdProductComparatorStickyComponent,
    NgClass,
    SdModalDefaultComponent,
    SdButtonComponent,
    TranslatePipe
  ]
})
export class StickyProductComparatorComponent implements OnInit, OnDestroy {
  products: IProductCard[];
  isSticky: boolean;
  maxProducts = 4;
  isWrongCategoryModalOpened = false;

  private readonly destroy$: Subject<void> = new Subject();

  private differentCategoryProduct: ComparedProductCard;
  constructor(
    private productComparatorService: ProductComparatorService,
    private router: Router,
    private scrollSpyService: ScrollSpyService,
    private toasterService: ToasterService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.productComparatorService.products$.pipe(takeUntil(this.destroy$)).subscribe((products) => {
      if (this.products !== undefined && products.length > this.products.length) {
        this.toasterService.sendToasterEvent({
          status: StatusEnum.success,
          title: 'comparator.toast.addedTitle',
          description: 'comparator.toast.addedDescription'
        });
      }
      this.products = [...products];
      this.cdRef.markForCheck();
    });

    this.scrollSpyService.activeSpyTarget.pipe(takeUntil(this.destroy$)).subscribe((spyTarget) => {
      this.isSticky = spyTarget !== 'footer';
      this.cdRef.markForCheck();
    });

    this.productComparatorService.maxProducts$.pipe(takeUntil(this.destroy$)).subscribe((max) => {
      this.maxProducts = max;
      this.cdRef.markForCheck();
    });

    this.productComparatorService.maxLengthError$.pipe(takeUntil(this.destroy$)).subscribe(() =>
      this.toasterService.sendToasterEvent({
        status: StatusEnum.error,
        title: 'comparator.toast.maxLengthErrorTitle',
        description: 'comparator.toast.maxLengthErrorDescription'
      })
    );

    this.productComparatorService.wrongCategoryError$.pipe(takeUntil(this.destroy$)).subscribe((product) => {
      this.differentCategoryProduct = product;
      this.isWrongCategoryModalOpened = true;
      this.cdRef.markForCheck();
    });
  }

  removeProduct(productCode: string): void {
    this.productComparatorService.removeProduct(productCode);
  }

  goToComparator(): void {
    if (this.products?.length > 1) {
      this.router.navigate(['/product-comparator']);
    } else {
      this.toasterService.sendToasterEvent({
        status: StatusEnum.error,
        title: 'comparator.toast.minLengthErrorTitle',
        description: 'comparator.toast.minLengthErrorDescription'
      });
    }
  }

  addDifferentCategory(): void {
    this.productComparatorService.emptyProducts();
    this.isWrongCategoryModalOpened = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
